import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { end: 100, suffix: '%', label: 'Client Focused' },
    { end: 24, suffix: '/7', label: 'Support Available' },
    { end: 10, suffix: '+', label: 'Years Combined Experience' },
    { end: 100, suffix: '%', label: 'Commitment' },
  ];

  const values = [
    { title: 'Excellence', desc: 'We pursue the highest standards in everything we do.' },
    { title: 'Integrity', desc: 'We build trust through transparency and ethical conduct.' },
    { title: 'Innovation', desc: 'We embrace creative thinking to solve complex challenges.' },
    { title: 'Partnership', desc: 'We work alongside clients as true partners in success.' },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Header */}
      <section className="py-16 lg:py-24">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">About Us</p>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Driving Business <span className="text-primary">Excellence</span>
              </h1>
              <p className="text-lg text-slate-400 mb-6">
                Professional Consulting Groups LLC was founded in 2025 in Moorhead, Minnesota with a singular mission:
                to help organizations navigate complexity and achieve transformative growth.
              </p>
              <p className="text-slate-400">
                We partner with businesses across the Midwest and beyond, from growing startups to
                established enterprises. Our team of industry experts brings deep sector knowledge
                and proven methodologies to every engagement.
              </p>
            </div>
            <div className="relative">
              <div className="bg-dark-800 rounded-3xl p-8 border border-white/5">
                <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-6">
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-semibold mb-3 font-sans">Our Approach</h3>
                <p className="text-slate-400">
                  Data-driven insights combined with human expertise to deliver transformative results
                  that last. We don't just advise—we roll up our sleeves and work alongside your team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section ref={statsRef} className="py-16 bg-dark-800">
        <div className="section-container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-6">
                <div className="text-4xl lg:text-5xl font-bold text-primary mb-2">
                  {isVisible ? <Counter end={stat.end} suffix={stat.suffix} /> : '0'}
                </div>
                <div className="text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24">
        <div className="section-container">
          <div className="text-center mb-16">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Our Values</p>
            <h2 className="text-3xl lg:text-4xl font-bold">What Drives Us</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="card p-6 text-center">
                <h3 className="text-xl font-semibold text-primary mb-3 font-sans">{value.title}</h3>
                <p className="text-slate-400 text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="section-container">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Want to Learn More?</h2>
            <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
              Meet our team of experts or get in touch to discuss how we can help your organization.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact" className="btn-primary">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const Counter = ({ end, suffix = '' }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 2000;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [end]);

  return <>{count}{suffix}</>;
};

export default About;
